import useAuth from '@app/auth/use-auth';
import {
  ORGANISATION_SEARCH_MIN_LENGTH,
  useGetOrganisationSearch,
} from '@shared/api/hooks/account/organisations';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { useDebounce } from '@shared/components/data-table/hooks/use-debounce';
import { DropdownMenuItem } from '@shared/components/ui/dropdown-menu';
import { cn } from '@shared/lib/utils';
import { useRef, useState } from 'react';

import { orgIcon } from './org-icon';

/** Keys the dropdown handles itself rather than the search box. */
const MENU_KEYS = ['Escape', 'Tab'];

type OrgSearchProps = {
  disabled: boolean;
  onSelect: (organisationUuid: string) => void;
};

/**
 * Type-to-search over every organisation, for staff.
 *
 * Renders no organisations until a query is entered, so opening the switcher
 * never puts a list of client names on screen.
 */
export function OrgSearch({ disabled, onSelect }: OrgSearchProps) {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const { data, isFetching } = useGetOrganisationSearch({
    search: debouncedSearch || undefined,
  });

  const results = data?.results ?? [];
  const belowMinimum = debouncedSearch.length < ORGANISATION_SEARCH_MIN_LENGTH;
  // The query trails the input by the debounce, so a stale result set would
  // otherwise flash against a query the user has already moved on from.
  const settled = search === debouncedSearch && !isFetching;

  const emptyMessage = () => {
    if (belowMinimum) return 'Search to switch org';
    if (!settled) return 'Searching...';
    return 'No organisations found';
  };

  const resultItems = () =>
    Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([data-disabled])'
      ) ?? []
    );

  return (
    <>
      <div
        className="flex items-center border-b px-2 pb-2"
        onKeyDown={(event) => {
          // The menu only moves focus into the list when the key lands on the
          // content element itself, and here it lands on the input, so step
          // into the results by hand.
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            const items = resultItems();
            if (!items.length) return;
            event.preventDefault();
            event.stopPropagation();
            (event.key === 'ArrowDown'
              ? items[0]
              : items[items.length - 1]
            ).focus();
            return;
          }
          // Escape and tab belong to the menu; everything else is held back
          // from its typeahead, which would otherwise swallow the query.
          if (!MENU_KEYS.includes(event.key)) event.stopPropagation();
        }}
      >
        <Icons.search className="mr-2 size-4 shrink-0 text-gray-500" />
        <input
          ref={inputRef}
          autoFocus
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onPointerDown={(event) => event.stopPropagation()}
          className="h-8 w-full bg-transparent text-sm outline-none"
        />
      </div>

      {!results.length || belowMinimum ? (
        <div className="py-6 text-center">
          <Text variant="subtle" className="text-muted-foreground">
            {emptyMessage()}
          </Text>
        </div>
      ) : (
        <div ref={listRef} className="-mx-1 max-h-80 overflow-y-auto p-1">
          {results.map((organisation) => {
            const current = organisation.uuid === user?.organizationUuid;
            const Icon = orgIcon(organisation.accountType);

            return (
              <DropdownMenuItem
                key={organisation.uuid}
                disabled={disabled}
                onSelect={(event) => {
                  if (current) {
                    event.preventDefault();
                    return;
                  }
                  onSelect(organisation.uuid);
                }}
                // The menu focuses whichever item the pointer is over, which
                // takes the caret out of the search box mid-word. Suppressing
                // that costs the focus-based highlight, so hover draws it.
                onPointerMove={(event) => event.preventDefault()}
                onKeyDown={(event) => {
                  const [first] = resultItems();
                  if (
                    event.key === 'ArrowUp' &&
                    event.currentTarget === first
                  ) {
                    event.preventDefault();
                    inputRef.current?.focus();
                  }
                }}
                className={cn(
                  'gap-2 hover:bg-accent hover:text-accent-foreground',
                  current &&
                    'bg-primary-50 text-primary-700 hover:bg-primary-50 hover:text-primary-700 focus:bg-primary-50 focus:text-primary-700'
                )}
              >
                <Icon className="size-4 shrink-0 text-gray-500" />
                <span className="truncate">{organisation.name}</span>
              </DropdownMenuItem>
            );
          })}
        </div>
      )}
    </>
  );
}
